/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert, Spin, Tag, message } from 'antd'
import BaseModal from 'src/components/ts-components/BaseModal'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import type {
  BalanceLineKind,
  BalanceRegistration,
  BalanceSlot,
  LockPreviewRow,
} from '@sportninja/common/services/teamBalance'
import { useLockPreview } from '@sportninja/common/features/team-balance/hooks/useLockPreview'
import colors from '@sportninja/common/constants/appColors'
import Price from 'src/components/ts-components/Price'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import { dollarsToMinor } from '../utils/balanceMath'
import { guardMessage } from '../utils/balanceError'
import { balanceLabel, balanceText, frequencyLabel } from '../utils/balanceI18n'
import { lockPreviewRowLabel } from '../utils/lockPreviewRowLabel'

type LockConfirmModalProps = {
  open: boolean
  teamId: string
  /** D40 — the sheet's roster; the preview is roster-scoped. */
  rosterId: string
  slot: BalanceSlot | null
  registration: Pick<
    BalanceRegistration,
    'installment_config' | 'currency'
  > | null
  submitting: boolean
  onConfirm: (previewFingerprint: string) => Promise<unknown>
  onClose: () => void
  /** Sep 23 — `official` for a team official's line; its preview needs the kind. */
  expectedKind?: BalanceLineKind
  /** Sep 23 — a team official's line names who pays, not "the player". */
  payerName?: string | null
}

const rowKey = (row: LockPreviewRow): string =>
  row.type === 'deposit' ? 'deposit' : `installment-${row.installment_number}`

/**
 * Confirms a slot lock before it generates the player's billing rows. R4-#4: the schedule
 * shown is the SERVER's exact, credit-aware preview (same schedule calculator + pre-lock-credit
 * allocator as the lock itself) — never a client approximation. A slot that cannot lock surfaces
 * the server's own 422 reason; a $0 slot shows "No payment due".
 *
 * Tax on top: every payment shows its share, the tax added to it and its total, and the footer
 * proves the player's share is unchanged with the tax on top of it. All of it is the server's
 * rows and `totals`; nothing is added up or taxed here.
 */
const LockConfirmModal = ({
  open,
  teamId,
  rosterId,
  slot,
  registration,
  submitting,
  onConfirm,
  onClose,
  expectedKind,
  payerName = null,
}: LockConfirmModalProps) => {
  const currencyCode = registration?.currency?.code ?? 'CAD'
  const { preview, isLoading, error, refetch } = useLockPreview({
    teamId,
    rosterId,
    slotId: open && slot ? slot.id : null,
    expectedKind,
  })

  const rows = preview?.rows ?? []
  const isZero = preview !== null && rows.length === 0
  const hasInstallments = rows.some((row) => row.type !== 'deposit')
  // D37: frequency comes from the FRESH server preview, never the React-Query-cached balance
  // config — a division Payments edit elsewhere must never show new rows under an old label.
  const frequency = frequencyLabel(preview?.installment_frequency)
  // Due dates are calendar days stored at midnight UTC, so they are read in UTC:
  // formatting one in the viewer's zone showed an Oct 15 payment as Oct 14 for
  // anyone west of UTC.
  const firstDue = rows.length > 0 ? formatShortDate(rows[0].due_date) : null
  const creditSurplus = (preview?.credit_surplus_cents ?? 0) / 100
  const totals = preview?.totals ?? null

  return (
    <BaseModal
      isOpen={open}
      title={balanceLabel('common:lockSlot')}
      okText={balanceLabel('common:lockAndCreateBilling')}
      cancelText={balanceLabel('common:balanceCommsCancel')}
      disableOk={isLoading || Boolean(error) || !preview?.fingerprint}
      isLoading={submitting}
      onClose={onClose}
      desktopWidth='560px'
      onOk={() => {
        // R5-#2: the confirm carries the previewed fingerprint — the server refuses a lock
        // whose inputs changed since this schedule was shown. R6-#3: any rejection refetches
        // the preview, so the modal immediately shows the CURRENT schedule instead of letting
        // a stale fingerprint be resubmitted forever. F4: the fingerprint is REQUIRED — the OK
        // button is disabled without one, and this guard makes a blind lock structurally impossible.
        const fingerprint = preview?.fingerprint
        if (!fingerprint) {
          return
        }
        onConfirm(fingerprint).catch((confirmError) => {
          message.error(guardMessage(confirmError))
          refetch()
        })
      }}
      destroyOnClose
    >
      {slot === null ? null : isLoading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 16px 0;
          `}
          data-testid='lock-preview-loading'
        >
          <Spin />
        </div>
      ) : error ? (
        <Alert
          type='warning'
          showIcon
          message={guardMessage(error)}
          data-testid='lock-preview-error'
        />
      ) : isZero ? (
        <p data-testid='lock-zero-note'>
          {balanceText('common:balanceLockZeroNote')}
        </p>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 10px;
          `}
        >
          <p
            css={css`
              margin: 0;
              ${paragraphSmall};
              color: ${colors.NEUTRAL_0};
            `}
          >
            {balanceText('common:balanceLockCreatesBilling')}
          </p>
          {/* SECONDARY_300 rules were invisible: BaseModal paints the body
              SECONDARY_300 too, so the rows ran together. */}
          <table css={scheduleTableStyle} data-testid='lock-preview-table'>
            <thead>
              <tr>
                <th>{balanceLabel('common:balanceColPayment')}</th>
                {/* SN-8310 — every payment's own date, not just the first:
                    a custom plan's dates are the point of it. */}
                <th>{balanceLabel('common:balanceColDue')}</th>
                <th css={numericStyle}>
                  {balanceLabel('common:balanceColShare')}
                </th>
                <th css={numericStyle}>
                  {balanceLabel('common:balanceColTax')}
                </th>
                <th css={numericStyle}>
                  {balanceLabel('common:balanceColTotal')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  data-testid={`lock-preview-${rowKey(row)}`}
                >
                  <td>
                    {lockPreviewRowLabel(row, preview?.schedule_shape)}
                    {row.settled_by_credit ? (
                      <Tag
                        color='green'
                        css={css`
                          margin-left: 8px;
                        `}
                      >
                        {balanceLabel('common:balanceLockCreditCovered')}
                      </Tag>
                    ) : row.credit_applied > 0 ? (
                      <Tag
                        color='green'
                        css={css`
                          margin-left: 8px;
                        `}
                      >
                        {balanceText('common:balanceLockCreditPartial', {
                          amount: row.credit_applied.toFixed(2),
                        })}
                      </Tag>
                    ) : null}
                  </td>
                  <td
                    css={numericStyle}
                    data-testid={`lock-preview-due-${rowKey(row)}`}
                  >
                    {formatShortDate(row.due_date)}
                  </td>
                  <td css={numericStyle}>
                    <Price
                      amount={dollarsToMinor(row.subtotal)}
                      currency={currencyCode}
                    />
                  </td>
                  <td css={numericStyle}>
                    <Price
                      amount={dollarsToMinor(row.tax)}
                      currency={currencyCode}
                    />
                  </td>
                  <td css={[numericStyle, totalCellStyle]}>
                    <Price
                      amount={dollarsToMinor(row.total)}
                      currency={currencyCode}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Absent on an older server, which sends the rows only. */}
            {totals ? (
              <tfoot>
                <tr data-testid='lock-preview-totals'>
                  <td>
                    {payerName
                      ? balanceText('common:balanceLockOfficialPays', {
                          name: payerName,
                        })
                      : balanceLabel('common:balanceLockPlayerPays')}
                  </td>
                  <td />
                  <td css={numericStyle}>
                    <Price
                      amount={dollarsToMinor(totals.subtotal)}
                      currency={currencyCode}
                    />
                  </td>
                  <td css={numericStyle}>
                    <Price
                      amount={dollarsToMinor(totals.tax)}
                      currency={currencyCode}
                    />
                  </td>
                  <td css={numericStyle}>
                    <Price
                      amount={dollarsToMinor(totals.total)}
                      currency={currencyCode}
                    />
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 2px;
              ${paragraphSmall};
              color: ${colors.NEUTRAL_50};
            `}
            data-testid='lock-schedule-summary'
          >
            {frequency && hasInstallments ? (
              <span>
                {balanceText('common:balanceLockFrequency', { frequency })}
              </span>
            ) : null}
            {firstDue ? (
              <span>
                {balanceText('common:balanceLockFirstDue', {
                  date: firstDue,
                })}
              </span>
            ) : null}
            {creditSurplus > 0 ? (
              <span data-testid='lock-credit-surplus'>
                {balanceText('common:balanceLockCreditSurplus', {
                  amount: creditSurplus.toFixed(2),
                })}
              </span>
            ) : null}
          </div>
          <p
            css={css`
              margin: 0;
              color: ${colors.NEUTRAL_70};
              ${paragraphXSmall};
            `}
          >
            {balanceText('common:balanceLockTaxNote')}
          </p>
        </div>
      )}
    </BaseModal>
  )
}

export default LockConfirmModal

/**
 * SECONDARY_100 rules, not SECONDARY_300: BaseModal paints the body SECONDARY_300
 * too, so rules in that colour ran the rows together.
 */
const scheduleTableStyle = css`
  width: 100%;
  border-collapse: collapse;
  ${paragraphSmall};

  th,
  td {
    padding: 8px 4px;
    border-bottom: 1px solid ${colors.SECONDARY_100};
    color: ${colors.NEUTRAL_0};
    text-align: right;
  }

  /* The payment name and its due date read left; every money column lines up
     on the right. */
  th:nth-of-type(-n + 2),
  td:nth-of-type(-n + 2) {
    text-align: left;
  }

  th {
    ${paragraphXSmall};
    ${weightSemiBold};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.NEUTRAL_50};
  }

  tfoot td {
    ${weightSemiBold};
    border-bottom: none;
  }
`

const numericStyle = css`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const totalCellStyle = css`
  ${weightSemiBold};
`
